import { Check, CheckCheck, ChevronDown, ExternalLink, FileCode2, HelpCircle, MapPin, Undo2, XCircle } from 'lucide-react';
import { useId, useState } from 'react';
import { cn } from '../../lib/cn';
import { CATEGORY_LABEL, FINDING_STATUS_LABEL } from '../../lib/format';
import type { Finding, FindingStatus } from '../../types/api';
import { CodeBlock } from '../CodeBlock';
import { Badge, PotentialBadge, SeverityBadge } from '../ui/Badges';
import { Button } from '../ui/Button';

const SEV_BORDER: Record<Finding['severity'], string> = {
  critical: 'border-l-sev-critical',
  high: 'border-l-sev-high',
  medium: 'border-l-sev-medium',
  low: 'border-l-sev-low',
  info: 'border-l-sev-info',
};

const STATUS_STYLE: Record<FindingStatus, string> = {
  open: 'bg-raised text-muted ring-line',
  accepted: 'bg-chalk-blue/15 text-chalk-blue ring-chalk-blue/40',
  dismissed: 'bg-raised text-faint ring-line line-through',
  resolved: 'bg-ok/10 text-ok ring-ok/30',
};

interface Props {
  finding: Finding;
  selectable: boolean;
  selected: boolean;
  onSelect: (selected: boolean) => void;
  onStatus: (status: FindingStatus) => void;
  onDismiss: () => void;
  busy?: boolean;
  defaultOpen?: boolean;
}

export function FindingCard({ finding: f, selectable, selected, onSelect, onStatus, onDismiss, busy, defaultOpen = false }: Props) {
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = useId();
  const dismissed = f.status === 'dismissed';

  return (
    <article className={cn('card border-l-4 transition-opacity', SEV_BORDER[f.severity], dismissed && 'opacity-60')} aria-label={f.title}>
      <div className="flex items-start gap-3 p-4">
        {selectable && (
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 accent-[rgb(var(--chalk-yellow))]"
            checked={selected}
            disabled={dismissed || !!f.publishedAt}
            onChange={(e) => onSelect(e.target.checked)}
            aria-label={`Select "${f.title}" for publishing`}
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <SeverityBadge severity={f.severity} />
            <Badge>{CATEGORY_LABEL[f.category]}</Badge>
            <PotentialBadge />
            {f.status !== 'open' && <Badge className={STATUS_STYLE[f.status]}>{FINDING_STATUS_LABEL[f.status]}</Badge>}
            {f.publishedAt && <Badge className="bg-ok/10 text-ok ring-ok/30">Published</Badge>}
          </div>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-controls={bodyId}
            className="mt-2 flex w-full items-start justify-between gap-3 text-left"
          >
            <h3 className="text-[0.95rem] font-semibold leading-snug">{f.title}</h3>
            <ChevronDown className={cn('mt-0.5 h-4 w-4 shrink-0 text-faint transition-transform', open && 'rotate-180')} aria-hidden />
          </button>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted">
            <span className="inline-flex min-w-0 items-center gap-1 font-mono">
              <FileCode2 className="h-3.5 w-3.5 shrink-0" aria-hidden />
              <span className="truncate">
                {f.filePath}
                {f.line !== undefined && `:${f.line}`}
              </span>
            </span>
            <span
              className="inline-flex items-center gap-1"
              title={
                f.locationCheck === 'verified-line'
                  ? 'The cited line exists in this PR’s diff.'
                  : 'The model’s line number could not be matched to the diff, so this finding applies to the file.'
              }
            >
              <MapPin className="h-3 w-3" aria-hidden />
              {f.locationCheck === 'verified-line' ? 'Line verified in diff' : 'File-level (line not verified)'}
            </span>
            {f.confidence !== undefined && <span>Confidence {Math.round(f.confidence * 100)}%</span>}
            {f.needsContext && (
              <span className="inline-flex items-center gap-1 text-warn">
                <HelpCircle className="h-3 w-3" aria-hidden />
                Needs context beyond the diff
              </span>
            )}
          </div>
        </div>
      </div>

      {open && (
        <div id={bodyId} className="space-y-4 border-t border-line px-4 py-4 sm:pl-11">
          <section>
            <h4 className="label">Explanation</h4>
            <p className="mt-1 whitespace-pre-wrap text-sm">{f.explanation}</p>
          </section>
          {f.evidence && <CodeBlock label="Evidence from the diff" code={f.evidence} />}
          {f.impact && (
            <section>
              <h4 className="label">Potential impact</h4>
              <p className="mt-1 whitespace-pre-wrap text-sm">{f.impact}</p>
            </section>
          )}
          {f.suggestedFix && <CodeBlock label="Suggested fix (proposal — review before applying)" code={f.suggestedFix} copyable />}
          {dismissed && f.dismissReason && (
            <p className="text-xs text-muted">
              <span className="font-medium">Dismissed:</span> {f.dismissReason}
            </p>
          )}
          {f.publishedCommentUrl && (
            <a href={f.publishedCommentUrl} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1 text-xs text-accent hover:underline">
              View comment on GitHub <ExternalLink className="h-3 w-3" />
            </a>
          )}

          <div className="flex flex-wrap gap-2 pt-1">
            {f.status === 'open' ? (
              <>
                <Button size="sm" disabled={busy} onClick={() => onStatus('accepted')} icon={<Check className="h-3.5 w-3.5" />}>
                  Accept
                </Button>
                <Button size="sm" disabled={busy} onClick={() => onStatus('resolved')} icon={<CheckCheck className="h-3.5 w-3.5" />}>
                  Mark resolved
                </Button>
                <Button size="sm" variant="ghost" disabled={busy} onClick={onDismiss} icon={<XCircle className="h-3.5 w-3.5" />}>
                  Dismiss
                </Button>
              </>
            ) : (
              <>
                {f.status === 'accepted' && (
                  <Button size="sm" disabled={busy} onClick={() => onStatus('resolved')} icon={<CheckCheck className="h-3.5 w-3.5" />}>
                    Mark resolved
                  </Button>
                )}
                <Button size="sm" variant="ghost" disabled={busy} onClick={() => onStatus('open')} icon={<Undo2 className="h-3.5 w-3.5" />}>
                  Reopen
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </article>
  );
}
